<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" md="6" xl="3">
        <v-tabs v-model="filterTab" @change="reloadItems">
          <v-tab>{{ $t('currentRights.all') }}</v-tab>
          <v-tab>{{ $t('currentRights.actionRequired') }}</v-tab>
          <v-tab>{{ $t('currentRights.doubleClaim') }}</v-tab>
        </v-tabs>
      </v-col>

      <v-col cols="12" md="6" xl="3">
        <v-text-field :label="$t('rights.search')" v-model="searchQuery" class="t-search-query">
          <v-icon slot="append" color="grey">mdi-magnify</v-icon>
        </v-text-field>
      </v-col>

      <v-col cols="12" xl="6">
        <v-toolbar class="shrink-bar float-right" dense elevation="0">
          <v-menu :close-on-content-click="false">
            <template v-slot:activator="menuScope">
              <v-tooltip top>
                <template v-slot:activator="tooltipScope">
                  <v-btn v-on="{ ...menuScope.on, ...tooltipScope.on }" :small="$vuetify.breakpoint.smAndDown" class="mx-1 my-2">
                    <v-icon left>mdi-view-column</v-icon>
                    <span>{{ $vuetify.breakpoint.mdAndDown ? $t('rights.columnsButtonShort') : $t('rights.columnsButton') }}</span>
                  </v-btn>
                </template>
                <span>{{ $t('rights.columnsButtonTooltip') }}</span>
              </v-tooltip>
            </template>

            <v-list dense>
              <v-list-item v-for="col in additionalColumns" :key="col.value">
                <v-list-item-action>
                  <v-checkbox
                    :value="selectedColumns.includes(col.value)"
                    @change="toggleColumnSelect(col.value)"
                  ></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  {{ col.text }}
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-tooltip top>
            <template v-slot:activator="tooltipScope">
              <v-btn v-bind="tooltipScope.attrs" v-on="tooltipScope.on" :small="$vuetify.breakpoint.smAndDown" class="mx-1 my-2" color="green" @click="addRightVisible = true">
                <v-icon color="white">mdi-plus</v-icon>
                <span class="text--white">{{ $vuetify.breakpoint.mdAndDown ? $t('rights.create') : $t('rights.create') }} </span>
              </v-btn>
            </template>
            <span>{{ $t('rights.createTooltip') }}</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="tooltipScope">
              <v-btn v-on="tooltipScope.on" :small="$vuetify.breakpoint.smAndDown" class="mx-1 my-2" color="primary" @click="onEditClick">
              <v-icon left>mdi-pencil</v-icon>
              <span>{{ $vuetify.breakpoint.mdAndDown ? $t('rights.editShort') : $t('rights.edit')}}</span>
            </v-btn>
            </template>
            <span>{{ $t('rights.editTooltip') }}</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="tooltipScope">
              <v-btn v-on="tooltipScope.on" :small="$vuetify.breakpoint.smAndDown" class="mx-1 my-2" color="primary" @click="onRevokeClick">
                <v-icon left>mdi-backup-restore</v-icon>
                <span>{{ $vuetify.breakpoint.mdAndDown ? $t('rights.revokeShort') : $t('rights.revoke')}}</span>
              </v-btn>
            </template>
            <span>{{ $t('rights.revokeTooltip') }}</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="tooltipScope">
              <v-btn v-on="tooltipScope.on" :disabled="downloading" :small="$vuetify.breakpoint.smAndDown" class="mx-1 my-2" color="primary" @click="onDownloadRights">
                <v-icon v-if="downloading" left>mdi-loading mdi-spin</v-icon>
                <v-icon v-else left>mdi-download</v-icon>
                <span>{{ $vuetify.breakpoint.mdAndDown ? $t('rights.downloadShort') : $t('rights.download') }}</span>
              </v-btn>
            </template>
            <span>{{ $t('rights.downloadTooltip') }}</span>
          </v-tooltip>
        </v-toolbar>
      </v-col>
    </v-row>

    <RightsTable
      :items="rightsList"
      :displayed-columns="displayedColumns"
      :checkedItems.sync="checkedItems"
      :continuos-amount="!currentRights.finished"
      :portion-loading="portionLoading"
      :total-count="currentRights.totalCount"
      @load-more="loadMoreItems"
    >

    </RightsTable>

    <v-dialog v-model="editFormVisible" max-width="60%" persistent>
      <EditRightForm
        v-if="editFormVisible"
        :items="editingRows"
        :actions-loading="editInProcess"
        :actions-loading-progress="editProgress.value"
        @close="onCancelEdit"
        @save="onEditRights"
      />
    </v-dialog>

    <v-dialog width="50%" max-width="1000px" v-model="addRightVisible">
      <AddRightForm
        v-if="addRightVisible"
        @cancel="addRightVisible = false"
        @save="onCreateRightFinished"
      />
    </v-dialog>

    <v-dialog v-model="addingToStaging" width="50%" persistent>
      <v-card>
        <v-card-title>
          Preparing rows to edit...
        </v-card-title>
        <v-card-text>
          <v-progress-linear
            indeterminate
            color="primary"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import {mapGetters} from "vuex";
import RightsTable from "@/components/rights/RightsTable";
import EditRightForm from "@/components/rights/EditRightForm";
import DebounceSearchLauncherMixin from "@/components/mixins/DebounceSearchLauncherMixin";
import RightsholderInitedDataDrivenMixin from "@/components/mixins/RightsholderInitedDataDrivenMixin";
import downloadFile from "@/utils/downloadFile";
import CreateRightButtonMixin from "@/components/mixins/CreateRightButtonMixin";
import AddRightForm from "@/components/rights/AddRightForm";
import Right from "@/models/Right";

export default {
  name: 'CurrentRights',
  components: {AddRightForm, EditRightForm, RightsTable},
  mixins: [DebounceSearchLauncherMixin, RightsholderInitedDataDrivenMixin, CreateRightButtonMixin],
  data() {
    return {
      selectedColumns: [],
      editFormVisible: false,
      checkedItems: [],
      editingRows: [],
      addingToStaging: false,
      portionLoading: false,
      filterTab: 0,
      editInProcess: false,
      addRightVisible: false,
      editProgress: {
        value: 0
      },
      downloading: false
    }
  },
  computed: {
    ...mapGetters('app', ['currentRightsholder']),
    currentRights() {
      return this.$store.getters["app/currentRights"];
    },

    rightsList() {
      if (!this.currentRights.list) {
        return [];
      }
      return this.currentRights.list.concat(this.currentRights.finished ? [] : [null]);
    },
    handlingItems() {
      return this.checkedItems.length ? this.checkedItems : this.currentRights.list;
    },
    additionalColumns() {
      const getCol = (value) => ({value, text: this.$t(`rights.columns.${value}`)})
      return ['rowNumber', 'spnr', 'version', 'year', 'labelName', 'externalId', 'bol', 'grp', 'cbskl', 'clnr', 'dur', 'dc'].map(getCol);
    },

    allColumns() {
      return [
        'rowNumber',
        'isrc',
        'type',
        'spnr',
        'artistName',
        'title',
        'countryCode',
        'year',
        'claimStartDate',
        'claimEndDate',
        'labelName',
        'status',
        'version',
        'externalId',
        'bol',
        'grp',
        'cbskl',
        'clnr',
        'dur',
        'dc'
      ].concat(this.currentRightsholder.internationalCollectionAllowed ? ['internationalCollection'] : []);
    },

    displayedColumns() {
      return this.allColumns.filter((col) => (this.additionalColumns.some(({value}) => value === col) ? this.selectedColumns.includes(col) : true));
    }
  },

  methods: {
    onCreateRightFinished(data) {
      this.onCreateRight(data)
        .then(() => {
          this.addRightVisible = false;
          this.reloadItems();
        })
    },

    // Override
    reloadItems() {
      this.loadRights();
    },

    // Override
    searchQueryUpdated() {
      this.loadRights();
    },

    onEditClick() {
      this.addingToStaging = true;
      this.$store.dispatch('app/addRightsToStaging', this.handlingItems)
        .then((result) => {
          this.editingRows = result.filter((x) => x['processing-status'] === 201)
            .map((x) => new Right({'!staged': true, ...x}));
          if (this.editingRows.length < this.handlingItems.length) {
            this.$toast(this.$t('currentRights.edit.addFailure', [this.handlingItems.length - this.editingRows.length, this.handlingItems.length]));
          }
          this.addingToStaging = false;
          //- No rows processed correctly
          if (this.editingRows.length === 0) {
            return
          }
          this.editFormVisible = true;
        })

    },

    onRevokeClick() {
      const items = this.handlingItems.length;
      this.$dialog.confirm({
        text: this.$t('currentRights.confirmRevoke', [items])
      })
        .then((revoke) => {
          if (revoke) {
            this.revokeRights();
          }
        })
    },

    revokeRights() {
      this.portionLoading = true;
      const items = this.handlingItems.map(({spnr, type}) => ({spnr, ityp: type, revoke: true}))
      this.$store.dispatch('app/revokeRights', {items})
        .then(() => {
          this.$toast(this.$t('currentRights.revoked'))
        })
        .finally(() => {
          this.portionLoading = false;
          this.reloadItems();
        })
    },

    loadMoreItems() {
      this.portionLoading = true;
      return this.$store.dispatch('app/loadRightsList', {search: this.searchQuery})
        .then(() => {
          this.portionLoading = false;
        });
    },

    loadRights() {
      if (!this.portionLoading === true) {
        this.portionLoading = true;
        if (this.$store.getters["app/currentRightsholderReady"]) {
          this.$store.dispatch('app/loadRightsList', {
            reload: true,
            search: this.searchQuery,
            action: ["", "rightaction", "DC"][this.filterTab]
          })
            .finally(() => {
              this.portionLoading = false;
            });
        }
      }
      return Promise.resolve('Already Loading')
    },

    toggleColumnSelect(col) {
      if (this.selectedColumns.includes(col)) {
        this.selectedColumns = this.selectedColumns.filter(c => c !== col);
      } else {
        this.selectedColumns.push(col);
      }
    },

    onDownloadRights() {
      this.downloading = true
      this.$store.dispatch('app/getRightsDownloadUrl',{search: this.searchQuery, action: ["", "rightaction", "DC"][this.filterTab], format: 'xlsx'})
        .then((res) => {
          downloadFile((res.filename ? res.filename : 'rights.xlsx'), res.url);
          this.downloading = false
        })
    },

    onCancelEdit() {
      this.editFormVisible = false;
      this.$store.dispatch('app/removeStagingRows', this.editingRows)
        .then(() => {
          this.editingRows = [];
        })
    },

    onEditRights(data) {

      const launchEdit = (items) => {
        this.editInProcess = true;
        this.editProgress = {value: 0};
        return this.$store.dispatch('app/editRightsList', {items, progressObservable: this.editProgress})
          .then((res) => {
            setTimeout(() => {
              this.editFormVisible = false;
              this.editInProcess = false;
              this.$toast(this.$t('rights.editForm.editedSuccessfully', [res.data.length]));
            }, 1000);
          })
          .catch((err) => {
            console.error(err);
            this.$toast(this.$t('rights.editForm.errorOnEdit'));
            this.editInProcess = false;
          })
      }

      switch (data.mode) {
        case 'list': {
          if (data.items.length) {
            launchEdit(data.items);
          } else {
            this.$toast(this.$t('rights.editForm.noItems'))
          }
          break;
        }
        case 'field': {
          if (Object.keys(data.item).length > 0) {
            const list = data.targets.map(({ityp, isrc, spnr}) => ({ityp, isrc, spnr, ...data.item}));
            launchEdit(list);
          } else {
            this.$toast(this.$t('rights.editForm.noChangesToApply'));
          }
        }
      }
    }
  },
}
</script>